import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ChevronLeft, Search, Filter, X, Download, Sparkles, BookText, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GradeWithDetails, Student, Subject, Class } from '../../types/supabase';

type FilterParams = {
  studentId?: string;
  subjectId?: string;
  classId?: string;
  examType?: string;
};

type SubjectAverage = {
  id: string;
  name: string;
  average: number;
  count: number;
  coefficient: number;
  className?: string;
  teaching_type: string;
};

const AdminGrades = () => {
  const [grades, setGrades] = useState<GradeWithDetails[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterParams>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [subjectAverages, setSubjectAverages] = useState<SubjectAverage[]>([]);
  const [generalAverage, setGeneralAverage] = useState<number>(0);
  const [professionalAverage, setProfessionalAverage] = useState<number>(0);
  const [generalSubjectAverage, setGeneralSubjectAverage] = useState<number>(0);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchGrades();
    fetchStudents();
    fetchSubjects();
    fetchClasses();
  }, []);

  // Update averages when grades or filters change
  useEffect(() => {
    calculateAverages(filteredGrades);
  }, [grades, filters, searchTerm]);

  const fetchGrades = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('grades')
        .select(`
          *,
          student:students(id, first_name, last_name, email),
          subject:subjects(*, class:classes(id, name, class_number, period))
        `)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setGrades(data as GradeWithDetails[]);
    } catch (error) {
      console.error('Error fetching grades:', error);
      setError('Erreur lors du chargement des notes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('last_name');

      if (error) {
        throw error;
      }

      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .order('name');

      if (error) {
        throw error;
      }

      setSubjects(data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .order('name')
        .order('class_number');

      if (error) {
        throw error;
      }

      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleFilterChange = (field: keyof FilterParams, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Function to calculate averages based on filtered grades
  const calculateAverages = (filteredGrades: GradeWithDetails[]) => {
    // Group grades by subject
    const subjectGrades: Record<string, GradeWithDetails[]> = {};
    const subjectMap: Record<string, {name: string, coefficient: number, className?: string, teaching_type: string}> = {};
    
    filteredGrades.forEach(grade => {
      if (!grade.is_absent && grade.grade !== null) {
        if (!subjectGrades[grade.subject_id]) {
          subjectGrades[grade.subject_id] = [];
          subjectMap[grade.subject_id] = {
            name: grade.subject.name,
            coefficient: grade.subject.coefficient || 1,
            className: grade.subject.class ? formatClassDisplay(grade.subject.class) : undefined,
            teaching_type: grade.subject.teaching_type || 'enseignement_general'
          };
        }
        subjectGrades[grade.subject_id].push(grade);
      }
    });
    
    // Calculate averages for each subject
    const averages: SubjectAverage[] = [];
    
    Object.keys(subjectGrades).forEach(subjectId => {
      const grades = subjectGrades[subjectId];
      const subject = subjectMap[subjectId];
      
      let totalWeightedGrade = 0;
      let totalCoefficient = 0;
      
      grades.forEach(grade => {
        if (!grade.is_absent && grade.grade !== null) {
          const coef = grade.coefficient || 1;
          totalWeightedGrade += grade.grade * coef;
          totalCoefficient += coef;
        }
      });
      
      const average = totalCoefficient > 0 
        ? Math.round((totalWeightedGrade / totalCoefficient) * 10) / 10 
        : 0;
      
      averages.push({
        id: subjectId,
        name: subject.name,
        average,
        count: grades.length,
        coefficient: subject.coefficient,
        className: subject.className,
        teaching_type: subject.teaching_type
      });
    });
    
    // Sort averages by subject name
    averages.sort((a, b) => a.name.localeCompare(b.name));
    
    // Calculate averages by teaching type
    const generalSubjects = averages.filter(s => s.teaching_type === 'enseignement_general');
    const professionalSubjects = averages.filter(s => s.teaching_type === 'enseignement_professionnel');

    // General subjects average
    if (generalSubjects.length > 0) {
      const generalCoef = generalSubjects.reduce((sum, s) => sum + s.coefficient, 0);
      const generalSum = generalSubjects.reduce((sum, s) => sum + s.average * s.coefficient, 0);
      const generalAvg = generalCoef > 0 ? generalSum / generalCoef : 0;
      setGeneralSubjectAverage(Math.round(generalAvg * 10) / 10);
    } else {
      setGeneralSubjectAverage(0);
    }
    
    // Professional subjects average
    if (professionalSubjects.length > 0) {
      const profCoef = professionalSubjects.reduce((sum, s) => sum + s.coefficient, 0);
      const profSum = professionalSubjects.reduce((sum, s) => sum + s.average * s.coefficient, 0);
      const profAvg = profCoef > 0 ? profSum / profCoef : 0;
      setProfessionalAverage(Math.round(profAvg * 10) / 10);
    } else {
      setProfessionalAverage(0);
    }
    
    // Overall average - combine ALL subjects (general + professional) with their coefficients
    if (averages.length > 0) {
      const totalCoef = averages.reduce((sum, s) => sum + s.coefficient, 0);
      const totalSum = averages.reduce((sum, s) => sum + s.average * s.coefficient, 0);
      const overallAvg = totalCoef > 0 ? totalSum / totalCoef : 0;
      setGeneralAverage(Math.round(overallAvg * 10) / 10);
    } else {
      setGeneralAverage(0);
    }
    
    setSubjectAverages(averages);
  };

  // Filters grades based on search term and applied filters
  const filteredGrades = grades.filter(grade => {
    const studentName = `${grade.student.first_name} ${grade.student.last_name}`.toLowerCase();
    const subjectName = grade.subject.name.toLowerCase();
    const examTitle = grade.exam_title?.toLowerCase() || '';
    
    // Basic text search
    const searchMatches = studentName.includes(searchTerm.toLowerCase()) || 
                         subjectName.includes(searchTerm.toLowerCase()) || 
                         examTitle.includes(searchTerm.toLowerCase());
    
    // Apply filters
    const studentMatches = !filters.studentId || grade.student_id === filters.studentId;
    const subjectMatches = !filters.subjectId || grade.subject_id === filters.subjectId;
    const classMatches = !filters.classId || 
                       (grade.subject.class && grade.subject.class.id === filters.classId);
    const examTypeMatches = !filters.examType || grade.exam_type === filters.examType;
    
    return searchMatches && studentMatches && subjectMatches && 
           classMatches && examTypeMatches;
  });

  const toggleGradeDetails = (gradeId: string) => {
    setSelectedGrade(selectedGrade === gradeId ? null : gradeId);
  };

  const toggleStatistics = () => {
    setShowStatistics(!showStatistics);
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Helper function to render grade or absent status
  const renderGradeValue = (grade: GradeWithDetails) => {
    if (grade.is_absent) {
      return <span className="font-semibold text-gray-500">ABS</span>;
    } else {
      return (
        <span 
          className={`font-semibold ${
            (grade.grade || 0) >= 10 ? 'text-success-500' : 'text-error-500'
          }`}
        >
          {grade.grade}/20
        </span>
      );
    }
  };

  // Helper function to get exam type display text
  const getExamTypeText = (type: string | null) => {
    if (!type) return '-';
    
    const types: Record<string, string> = {
      'devoir_surveille': 'Devoir surveillé',
      'devoir_maison': 'Devoir maison',
      'participation': 'Participation',
      'examen_blanc': 'Examen blanc',
      'examen_officiel': 'Examen officiel'
    };
    
    return types[type] || type;
  };

  // Helper function to format class display
  const formatClassDisplay = (classObj: any) => {
    if (!classObj) return '-';
    return `${classObj.name} ${classObj.class_number} (${classObj.period})`;
  };

  // Export to CSV function
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      // Prepare CSV data with all fields
      const csvData = filteredGrades.map(grade => ({
        'Étudiant': `${grade.student.first_name} ${grade.student.last_name}`,
        'Email Étudiant': grade.student.email,
        'Matière': grade.subject.name,
        'Type Enseignement': grade.subject.teaching_type === 'enseignement_general' ? 'Général' : 'Professionnel',
        'Classe': formatClassDisplay(grade.subject.class),
        'Note': grade.is_absent ? 'ABS' : grade.grade,
        'Coefficient': grade.coefficient || 1,
        'Absent': grade.is_absent ? 'Oui' : 'Non',
        'Type Examen': getExamTypeText(grade.exam_type),
        'Méthode': grade.exam_method === 'ecrit' ? 'Écrit' : grade.exam_method === 'oral' ? 'Oral' : '-',
        'Titre Examen': grade.exam_title || '-',
        'Date Examen': formatDate(grade.exam_date),
        'Commentaire': grade.comment || '-',
        'Date Création': formatDate(grade.created_at),
        'Date Modification': formatDate(grade.updated_at)
      }));

      // Convert to CSV
      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in CSV
            return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `notes_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Erreur lors de l\'export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <header className="flex justify-between items-center mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des notes</h1>
          <p className="mt-2 text-gray-600">
            Consultez et gérez toutes les notes du système
          </p>
        </div>
        
        <button
          onClick={exportToCSV}
          disabled={isExporting || filteredGrades.length === 0}
          className="btn btn-secondary inline-flex items-center"
        >
          <Download className="h-5 w-5 mr-2" />
          {isExporting ? 'Export...' : 'Export CSV'}
        </button>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative w-full md:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="input-field pl-10"
            placeholder="Rechercher par étudiant, matière ou examen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'} flex items-center space-x-1`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres avancés</span>
          </button>
          
          <button
            onClick={toggleStatistics}
            className={`btn ${showStatistics ? 'btn-primary' : 'btn-outline'} flex items-center space-x-1`}
          >
            <Sparkles className="h-4 w-4" />
            <span>Statistiques</span>
          </button>
          
          {Object.values(filters).some(value => value) && (
            <button 
              onClick={clearFilters}
              className="btn btn-outline flex items-center space-x-1 text-sm py-1 px-2"
            >
              <X className="h-4 w-4" />
              <span>Effacer</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="studentFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Étudiant:
              </label>
              <select
                id="studentFilter"
                value={filters.studentId || ''}
                onChange={(e) => handleFilterChange('studentId', e.target.value)}
                className="input-field"
              >
                <option value="">Tous les étudiants</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="subjectFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Matière:
              </label>
              <select
                id="subjectFilter"
                value={filters.subjectId || ''}
                onChange={(e) => handleFilterChange('subjectId', e.target.value)}
                className="input-field"
              >
                <option value="">Toutes les matières</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="classFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Classe:
              </label>
              <select
                id="classFilter"
                value={filters.classId || ''}
                onChange={(e) => handleFilterChange('classId', e.target.value)}
                className="input-field"
              >
                <option value="">Toutes les classes</option>
                {classes.map((classObj) => (
                  <option key={classObj.id} value={classObj.id}>
                    {classObj.name} {classObj.class_number} ({classObj.period})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="examTypeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Type d'examen:
              </label>
              <select
                id="examTypeFilter"
                value={filters.examType || ''}
                onChange={(e) => handleFilterChange('examType', e.target.value)}
                className="input-field"
              >
                <option value="">Tous les types</option>
                <option value="devoir_surveille">Devoir surveillé</option>
                <option value="devoir_maison">Devoir maison</option>
                <option value="participation">Participation</option>
                <option value="examen_blanc">Examen blanc</option>
                <option value="examen_officiel">Examen officiel</option>
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Statistics Panel */}
      {showStatistics && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Statistiques des notes</h3>
            <div className="text-sm text-gray-500">
              {Object.values(filters).some(value => value) && (
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs">
                  Filtres actifs
                </span>
              )}
            </div>
          </div>
          
          {/* Overall Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">Moyenne générale</p>
                  <p className="text-3xl font-bold mt-2">
                    {generalAverage}/20
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-full">
                  <Calendar className="h-6 w-6" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">Enseignement général</p>
                  <p className="text-3xl font-bold mt-2">
                    {generalSubjectAverage}/20
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-full">
                  <BookText className="h-6 w-6" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">Enseignement professionnel</p>
                  <p className="text-3xl font-bold mt-2">
                    {professionalAverage}/20
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 rounded-full">
                  <Sparkles className="h-6 w-6" />
                </div>
              </div>
            </div>
          </div>
          
          {subjectAverages.length > 0 ? (
            <div className="border-t border-gray-100 pt-4">
              <h4 className="font-semibold mb-3">Moyennes par matière</h4>
              
              {/* General Subjects */}
              <div className="mb-6">
                <h5 className="font-medium text-secondary-600 mb-3">Enseignement général</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subjectAverages
                    .filter(subject => subject.teaching_type === 'enseignement_general')
                    .map(subject => (
                    <div key={subject.id} className="border rounded-md p-3">
                      <div className="mb-1">
                        <div className="font-medium">{subject.name}</div>
                        {subject.className && (
                          <div className="text-xs text-gray-500">{subject.className}</div>
                        )}
                      </div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-600">
                          Coef. {subject.coefficient} ({subject.count} note{subject.count !== 1 ? 's' : ''})
                        </span>
                        <span 
                          className={`font-semibold ${
                            subject.average >= 10 ? 'text-success-500' : 'text-error-500'
                          }`}
                        >
                          {subject.average}/20
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            subject.average >= 10 ? 'bg-success-500' : 'bg-error-500'
                          }`}
                          style={{ width: `${(subject.average / 20) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Professional Subjects */}
              <div>
                <h5 className="font-medium text-accent-600 mb-3">Enseignement professionnel</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subjectAverages
                    .filter(subject => subject.teaching_type === 'enseignement_professionnel')
                    .map(subject => (
                    <div key={subject.id} className="border rounded-md p-3">
                      <div className="mb-1">
                        <div className="font-medium">{subject.name}</div>
                        {subject.className && (
                          <div className="text-xs text-gray-500">{subject.className}</div>
                        )}
                      </div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-600">
                          Coef. {subject.coefficient} ({subject.count} note{subject.count !== 1 ? 's' : ''})
                        </span>
                        <span 
                          className={`font-semibold ${
                            subject.average >= 10 ? 'text-success-500' : 'text-error-500'
                          }`}
                        >
                          {subject.average}/20
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            subject.average >= 10 ? 'bg-success-500' : 'bg-error-500'
                          }`}
                          style={{ width: `${(subject.average / 20) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              Aucune note disponible pour calculer les moyennes
            </div>
          )}
        </div>
      )}

      {/* Grades List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredGrades.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredGrades.map((grade) => (
              <div key={grade.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {grade.student.first_name} {grade.student.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {grade.subject.name} - {formatClassDisplay(grade.subject.class)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {grade.subject.teaching_type === 'enseignement_general' ? 'Enseignement général' : 'Enseignement professionnel'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {renderGradeValue(grade)}
                    <button
                      onClick={() => toggleGradeDetails(grade.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="text-sm">Détails</span>
                    </button>
                  </div>
                </div>
                
                {selectedGrade === grade.id && (
                  <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-2 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500">Type d'examen</p>
                        <p className="font-medium">{getExamTypeText(grade.exam_type)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium">{formatDate(grade.exam_date)}</p>
                      </div>
                      {grade.exam_title && (
                        <div>
                          <p className="text-gray-500">Titre</p>
                          <p className="font-medium">{grade.exam_title}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Coefficient</p>
                        <p className="font-medium">{grade.coefficient || 1}</p>
                      </div>
                      {grade.comment && (
                        <div className="md:col-span-2">
                          <p className="text-gray-500">Commentaire</p>
                          <p className="font-medium">{grade.comment}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {isLoading ? 'Chargement...' : 'Aucune note trouvée'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGrades;